import { Injectable, OnDestroy } from '@angular/core';
import { NavigationExtras, Router, Params } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { urlBuilder } from '@metromarkets/sdk';
import { PlatformLocation } from '@angular/common';
import get from 'lodash.get';
import isObject from 'lodash.isobject';

import { getQueryParams, RouterStateUrl } from '../../core/store';
import { QueryOptions } from '../models/query-options.interface';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '~app/modules/generic/constants';
import { MediaObserver } from "@angular/flex-layout";
import { MEDIA_ALISES } from "~shared/constants/media.constant";

export interface NavigationHelperConfig {
  resetPaging?: boolean;
  resetFilters?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationHelper implements OnDestroy {
  private queryParams: Params;
  private destroySubject$ = new Subject();
  private isLocationPopState: boolean;
  isDesktop = this.mediaObserver.isActive(MEDIA_ALISES.desktop);

  constructor(private router: Router,
              private location: PlatformLocation,
              private store: Store<RouterStateUrl>,
              private mediaObserver: MediaObserver) {
    this.store
      .pipe(select(getQueryParams))
      .pipe(takeUntil(this.destroySubject$))
      .subscribe((queryParams: QueryOptions) => (this.queryParams = queryParams));

    this.location.onPopState(() => (this.isLocationPopState = true));
  }

  ngOnDestroy() {
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }

  getCurrentUrl(): string {
    return this.router.url;
  }

  replaceUrl(commands: any[], extras: NavigationExtras, config: NavigationHelperConfig = {}): void {
    const { queryParams: { offset = DEFAULT_OFFSET, limit = DEFAULT_LIMIT, ...restQueries } = {}, ...restExtras } =
      extras;
    const { resetPaging = false, resetFilters = false } = config;

    const resetPagination =
      resetPaging && !this.isLocationPopState ? { offset: DEFAULT_OFFSET, limit: DEFAULT_LIMIT } : { offset, limit };

    const nextQueryParams = this.updateQueryParams({ ...restQueries, ...resetPagination }, resetFilters);

    const nextExtras = {
      ...restExtras,
      queryParams: urlBuilder.encode(nextQueryParams),
    };

    this.router.navigate(commands, nextExtras).then(() => (this.isLocationPopState = false));
  }

  private updateQueryParams(nextQuery: QueryOptions, resetFilters?: boolean): QueryOptions {
    const { filter: prevFilter, ...restPrev } = this.queryParams || {};
    const { filter, ...rest } = nextQuery;

    // Refactor it more when we rollout A/B tests we have
    const prevCategorySlugs = get(prevFilter, 'categories');

    if (resetFilters) {
      return {
        ...restPrev,
        ...rest,
      };
    }

    const nextCategorySlugs = get(filter, 'categories');
    const categories = this.getCategoryOrCategories(prevCategorySlugs, nextCategorySlugs);

    return {
      ...restPrev,
      ...rest,
      filter: {
        ...filter,
        categories,
      },
    };
  }

  private getCategoryOrCategories(prevCategorySlugs: string[], currentCategorySlugs: string[]) {
    const categories = currentCategorySlugs || prevCategorySlugs;
    if (!categories) {
      return undefined;
    }

    if (isObject(categories)) {
      return { ...categories };
    }

    return { 0: categories };
  }
}
